import { useContext, useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import {
  useFeedbackPanel,
  type FeedbackPanelSession,
} from '@/context/feedback-panel.context';
import { useToast } from '@/context/ToastContext';
import { useSubmitFeedback } from '@/api/rpc/hooks/core/feedback/useSubmitFeedback';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCanViewFeedbackPage } from '@/hooks/useCanViewFeedbackPage';
import paths from '@/paths';
import type { FeedbackKind } from '@erp/shared-types';
import { FeedbackKindSelect } from '@/pages/authenticated/feedback-triage/FeedbackKindBadge';
import { DraftAttachmentsSection } from './DraftAttachmentsSection';
import { FeedbackPanelFrame } from './FeedbackPanelFrame';
import { ReportingForField } from './ReportingForField';
import {
  MAX_FEEDBACK_IMAGES,
  buildFeedbackContext,
  buildPageInfo,
  readBrowserEnvironment,
  type ImageRejection,
} from './feedbackAttachmentRules';
import { useFeedbackAttachments } from './useFeedbackAttachments';

function useRejectionToast() {
  const gt = useGT();
  const { showToast } = useToast();
  return (rejected: ImageRejection[]) => {
    const first = rejected[0];
    if (!first) return;
    const reasons = {
      not_image: gt('{name} is not a supported image (PNG, JPEG, GIF, WebP).', {
        context: 'toast when a non-image file was dropped on the feedback',
        name: first.file.name,
      }),
      too_large: gt('{name} is larger than 10 MB.', {
        context: 'toast when an image is too large for the feedback',
        name: first.file.name,
      }),
      too_many: gt('You can attach up to {max} images.', {
        context: 'toast when too many images were added to the feedback',
        max: MAX_FEEDBACK_IMAGES,
      }),
    };
    showToast({
      title: gt('Some images were not added', {
        context: 'toast title when dropped files were rejected',
      }),
      message: reasons[first.reason],
      color: 'warning',
      icon: 'x',
    });
  };
}

function ManualPageUrlField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="feedback-page-url">
        <T context="label above the optional page URL input on a manually filed feedback">
          Page URL (optional)
        </T>
      </Label>
      <Input
        id="feedback-page-url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={gt('https://…', {
          context: 'placeholder for the optional page URL input',
        })}
        disabled={disabled}
      />
    </div>
  );
}

function FeedbackDraftFooter({
  onCancel,
  onSubmit,
  canSubmit,
  isPending,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  isPending: boolean;
}) {
  const canViewFeedbackPage = useCanViewFeedbackPage();
  return (
    <div className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-3">
      {canViewFeedbackPage && (
        <Link
          to={paths.authenticated.feedbackTriage}
          className="mr-auto text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          <T context="small link in the send-feedback panel to the feedback page">
            View all feedback
          </T>
        </Link>
      )}
      <Button variant="outline" onClick={onCancel} disabled={isPending}>
        <T context="cancel button in feedback dialog">Cancel</T>
      </Button>
      <Button onClick={onSubmit} disabled={!canSubmit}>
        {isPending ? (
          <T context="feedback submit button label while sending is in progress">
            Sending…
          </T>
        ) : (
          <T context="feedback submit button label">Send Feedback</T>
        )}
      </Button>
    </div>
  );
}

/** One draft. Remounted per session (see `key` in FeedbackPanel). */
function FeedbackDraft({ session }: { session: FeedbackPanelSession }) {
  const gt = useGT();
  const panel = useFeedbackPanel();
  const { currentUser } = useContext(AuthContext);
  const attachments = useFeedbackAttachments();
  const submit = useSubmitFeedback();
  const reportRejections = useRejectionToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [message, setMessage] = useState('');
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [manualPageUrl, setManualPageUrl] = useState('');
  const [reporterUserId, setReporterUserId] = useState<string | undefined>();

  // First screenshot on open, once the panel has painted so it is excluded.
  const capturePage = attachments.capturePage;
  useEffect(() => {
    if (session.mode === 'capture') capturePage();
    const focusTimer = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only; the session key remounts
  }, []);

  const reporterMissing = reporterUserId === '';
  const canSubmit =
    message.trim().length > 0 &&
    !attachments.isCapturing &&
    !submit.isPending &&
    !reporterMissing;

  async function handleSubmit() {
    if (!currentUser || !canSubmit) return;
    const page =
      session.mode === 'capture'
        ? buildPageInfo(session.pageUrl, session.pageTitle)
        : buildPageInfo(manualPageUrl, null);
    try {
      const { id } = await submit.mutateAsync({
        message: message.trim(),
        kind,
        ...page,
        context: buildFeedbackContext(readBrowserEnvironment()),
        attachments: attachments.collect(new Date()),
        reporterUserId: reporterUserId || undefined,
      });
      panel.notifySubmitted(id);
      // Only close what this draft owns: the user may have closed it and
      // started another while the request was in flight.
      if (panel.session?.id === session.id) panel.close();
    } catch {
      // The mutation hook already showed the error toast.
    }
  }

  return (
    <>
      <div
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
        onPaste={(e) => reportRejections(attachments.onPaste(e))}
      >
        <p className="text-xs text-muted-foreground">
          <T context="feedback panel intro text">
            Report an issue or share feedback with the support team. You can
            keep using the app and capture more screenshots while this stays
            open.
          </T>
        </p>

        <ReportingForField
          value={reporterUserId}
          onChange={setReporterUserId}
          disabled={submit.isPending}
        />

        <div className="flex flex-col gap-1">
          <Label htmlFor="feedback-kind">
            <T context="label above the feedback type select in the send-feedback panel">
              What kind of feedback is this?
            </T>
          </Label>
          <FeedbackKindSelect
            triggerId="feedback-kind"
            value={kind}
            onChange={(next) => {
              if (next) setKind(next);
            }}
            disabled={submit.isPending}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="feedback-message">
            <T context="label above feedback text input">
              What&apos;s the issue or feedback?
            </T>
          </Label>
          <Textarea
            id="feedback-message"
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={gt(
              "Describe what happened or what you'd like to see improved…",
              { context: 'placeholder text in the feedback textarea' },
            )}
            rows={4}
            maxLength={5000}
            disabled={submit.isPending}
          />
        </div>

        {session.mode === 'manual' && (
          <ManualPageUrlField
            value={manualPageUrl}
            onChange={setManualPageUrl}
            disabled={submit.isPending}
          />
        )}

        <DraftAttachmentsSection
          attachments={attachments}
          onRejected={reportRejections}
          disabled={submit.isPending}
        />
      </div>

      <FeedbackDraftFooter
        onCancel={panel.close}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        isPending={submit.isPending}
      />
    </>
  );
}

/**
 * The feedback mini-window. Mounted once at the authenticated layout; the
 * draft lives as long as the session, across page navigation.
 */
export function FeedbackPanel() {
  const panel = useFeedbackPanel();
  const { currentUser } = useContext(AuthContext);
  if (!panel.session || !currentUser) return null;

  return (
    <FeedbackPanelFrame
      title={<T context="feedback dialog title">Send Feedback</T>}
      minimized={panel.minimized}
      onToggleMinimized={() => panel.setMinimized(!panel.minimized)}
      onClose={panel.close}
    >
      <FeedbackDraft key={panel.session.id} session={panel.session} />
    </FeedbackPanelFrame>
  );
}
